import { Component, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { PageEvent } from '@angular/material/paginator';
import { Subject, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, catchError } from 'rxjs/operators';
import { Client } from '../Models/Client';
import { User } from '../Models/User';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AdminBeneficiaryDialogComponent } from '../admin-beneficiary-dialog/admin-beneficiary-dialog.component';
import { Router } from '@angular/router';

@Component({
  selector: 'app-admin-clients',
  templateUrl: './admin-clients.component.html',
  styleUrl: './admin-clients.component.css',
  standalone: false
})
export class AdminClientsComponent implements OnInit, OnDestroy {

  loggedInUser?: User;
  private loggedInUserTag = "";

  clients: Client[] = [];
  filteredClients: Client[] = [];
  expandedClientId: number | null = null;

  // mapping beneficiary id -> "Name (URN)" for quick display
  associatedBeneficiaryMap: Record<number, string> = {};

  // Cached list of PricingTier rows (Tier A, Tier B, …) loaded once on init.
  // Drives the per-client dropdown shown in edit mode and the view-mode label.
  pricingTiers: { id: number; name: string }[] = [];

  // Search supporting properties
  clientSearchTerm: string = '';
  clientUrnSearchTerm: string = '';
  private clientSearchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  // Pagination properties
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25, 50];

  constructor(private http: HttpClient, private dialog: MatDialog, private router: Router) { }

  ngOnInit() {
    // Load saved page size from localStorage
    const savedPageSize = localStorage.getItem('admin-clients-page-size');
    if (savedPageSize) {
      this.pageSize = +savedPageSize;
    }

    this.loggedInUser = this.router.lastSuccessfulNavigation?.extras?.state?.['user'] as User | undefined;
    this.loggedInUserTag = this.loggedInUser ? `${this.loggedInUser.lastName}, ${this.loggedInUser.firstName}` : '';

    // Setup auto-search with debounce
    this.setupAutoSearch();
    this.setupFinanceClientAutoSearch();

    // Initial load of all clients + the pricing tier list for the Tier column dropdown.
    this.getClients();
    this.loadPricingTiers();
  }

  private loadPricingTiers() {
    this.http.get<{ id: number; name: string }[]>('/charitycentral/GetPricingTiers')
      .subscribe({
        next: tiers => { this.pricingTiers = tiers || []; },
        error: err => { console.error('Error loading pricing tiers:', err); }
      });
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.financeClientSearchSubscription?.unsubscribe();
  }

  private setupAutoSearch() {
    this.searchSubscription = this.clientSearchSubject.pipe(
      debounceTime(500),
      switchMap(() => {
        const name = this.clientSearchTerm.trim();
        const urn = this.clientUrnSearchTerm.trim();

        let params = new HttpParams().set('user', this.loggedInUserTag);
        // Name needs 3+ chars to search; URN (an exact identifier) fires from 1 char.
        if (name.length >= 3) params = params.set('clientName', name);
        if (urn.length >= 1) params = params.set('beneficiaryUrn', urn);

        // If a name of only 1-2 chars is typed (and no URN), keep current results.
        if (name.length > 0 && name.length < 3 && urn.length === 0) {
          return of(this.clients);
        }

        // Otherwise search with whatever filters are provided (both empty => all clients).
        return this.http.get<Client[]>('/charitycentral/getallclients', { params }).pipe(
          catchError(error => {
            console.error('Error searching clients:', error);
            return of([]);
          })
        );
      })
    ).subscribe(result => {
      this.clients = result || [];
      this.applyClientFilter();
    });
  }

  // Called on each keystroke in the search input
  onClientSearchInput() {
    this.clientSearchSubject.next(this.clientSearchTerm);
  }

  // Called on each keystroke in the URN search input (reuses the same debounced pipeline)
  onClientUrnSearchInput() {
    this.clientSearchSubject.next(this.clientUrnSearchTerm);
  }

  // Computed property for paginated clients
  get paginatedClients(): Client[] {
    const start = this.pageIndex * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredClients.slice(start, end);
  }

  // Handle page change events
  pageChanged(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    localStorage.setItem('admin-clients-page-size', String(event.pageSize));
  }

  // supports optional server-side search by name
  getClients(searchName?: string, searchUrn?: string) {
    let params = new HttpParams().set('user', this.loggedInUserTag);
    if (searchName && searchName.trim().length > 0) params = params.set('clientName', searchName.trim());
    if (searchUrn && searchUrn.trim().length > 0) params = params.set('beneficiaryUrn', searchUrn.trim());

    this.http.get<Client[]>('/charitycentral/getallclients', { params })
      .subscribe(result => {
        this.clients = result || [];
        this.applyClientFilter();
      });
  }

  applyClientFilter() {
    const term = (this.clientSearchTerm || '').trim().toLowerCase();
    if (!term) {
      this.filteredClients = [...this.clients];
    } else {
      this.filteredClients = this.clients.filter(c => (c.name || '').toLowerCase().includes(term));
    }
    // Reset to first page when filter changes
    this.pageIndex = 0;
  }

  onClientSearch() {
    const term = (this.clientSearchTerm || '').trim();
    const urn = (this.clientUrnSearchTerm || '').trim();
    if (term.length === 0 && urn.length === 0) {
      this.getClients();
    } else {
      this.getClients(term, urn);
    }
  }

  clearClientSearch() {
    this.clientSearchTerm = '';
    this.clientUrnSearchTerm = '';
    this.getClients();
  }

  onClientSearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onClientSearch();
    }
  }

  onClientOpened(client: Client) {
    this.expandedClientId = client.id;
  }

  onClientClosed(client: Client) {
    if (this.expandedClientId === client.id) {
      this.expandedClientId = null;
    }
  }


  addClient() {
    if (!this.clients.some(c => c.id ===0)) {
      const newClient: Client = { id:0, name: '', isEdit: true, associatedBeneficiaryId: undefined };

      this.clients.unshift(newClient);
      this.applyClientFilter();
      this.beneficiaryNameInput = '';
      this.expandedClientId = null;
    }
  }

  editClient(client: Client) {
    this.clients.forEach(c => c.isEdit = false);
    client.isEdit = true;
    this.beneficiaryNameInput = this.getAssociatedBeneficiaryDisplay(client) === 'No Associated Beneficiary' ? '' : this.getAssociatedBeneficiaryDisplay(client);
  }

  cancelEdit(client: Client) {
    client.isEdit = false;
    this.getClients();
  }

  saveClient(client: Client) {
    // Send only the client object with id pointer for associated beneficiary.
    const payload: any = { ...client };
    if (payload.associatedBeneficiaryName !== undefined) delete payload.associatedBeneficiaryName;
    if (payload.financeClientName !== undefined) delete payload.financeClientName;
    // pricingTierName is display-only — the backend resolves it from PricingTierId on the next read.
    if (payload.pricingTierName !== undefined) delete payload.pricingTierName;

    const params = new HttpParams()
      .set('client', JSON.stringify(payload))
      .set('user', this.loggedInUserTag);

    if (client.id ===0) {
      this.http.post<number>('/charitycentral/createclient', null, { params })
        .subscribe({
          next: () => this.getClients(),
          error: err => alert(`Failed to create client: ${err?.error?.message ?? err?.message ?? 'Unknown error'}`)
        });
    } else {
      this.http.post<boolean>('/charitycentral/updateclient', null, { params })
        .subscribe({
          next: () => this.getClients(),
          error: err => alert(`Failed to update client: ${err?.error?.message ?? err?.message ?? 'Unknown error'}`)
        });
    }
  }

  deleteClient(client: Client) {
    const ref = this.dialog.open(ConfirmDialogComponent, {
      data: { title: 'Delete Client', message: 'Are you sure you want to delete this client?', confirmText: 'Yes', cancelText: 'No' }
    });

    ref.afterClosed().subscribe(result => {
      if (result) {
        const params = new HttpParams()
          .set('clientId', client.id)
          .set('user', this.loggedInUserTag);

        this.http.post<boolean>('/charitycentral/deleteclient', null, { params })
          .subscribe(success => {
            if (success) {
              this.clients = this.clients.filter(c => c.id !== client.id);
              this.applyClientFilter();
            }
          });
      }
    });
  }

  // Finance Client lookup state
  isFinanceClientPopupOpen = false;
  financeClientLookupClient: Client | null = null;
  financeClientNameInput = '';
  financeClientSearchResults: any[] = [];
  financeClientXeroError = false;
  private financeClientSearchSubject = new Subject<string>();
  private financeClientSearchSubscription?: Subscription;

  // Create new Xero contact state (within the finance client popup)
  isCreatingFinanceContact = false;
  isCreatingFinanceContactSaving = false;
  newFinanceContact = {
    name: '',
    firstName: '',
    lastName: '',
    emailAddress: '',
    addressLine1: '',
    city: '',
    region: '',
    postalCode: '',
    country: ''
  };

  private setupFinanceClientAutoSearch() {
    this.financeClientSearchSubscription = this.financeClientSearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(term => {
        this.financeClientXeroError = false;
        if (term.length < 3) { this.financeClientSearchResults = []; return of([]); }
        return this.http.get<any[]>(
          `/charitycentral/GetFinanceClients?NameSearch=${encodeURIComponent(term)}&UserId=${encodeURIComponent(this.loggedInUserTag)}`
        ).pipe(catchError(err => {
          if (err.status === 401) this.financeClientXeroError = true;
          return of([]);
        }));
      })
    ).subscribe(results => this.financeClientSearchResults = results);
  }

  openFinanceClientLookup(client: Client) {
    this.financeClientLookupClient = client;
    this.financeClientNameInput = '';
    this.financeClientSearchResults = [];
    this.isFinanceClientPopupOpen = true;
  }

  closeFinanceClientLookup() {
    this.isFinanceClientPopupOpen = false;
    this.financeClientLookupClient = null;
    this.isCreatingFinanceContact = false;
    this.isCreatingFinanceContactSaving = false;
  }

  onFinanceClientNameInput(term: string) {
    this.financeClientSearchSubject.next(term);
  }

  selectFinanceContact(contact: any) {
    if (!this.financeClientLookupClient) return;
    this.financeClientLookupClient.financeClientId = contact.ContactID;
    this.financeClientLookupClient.financeClientName = contact.Name;
    this.closeFinanceClientLookup();
  }

  openCreateFinanceContact() {
    const c = this.financeClientLookupClient;
    this.newFinanceContact = {
      name: c?.name ?? '',
      firstName: '',
      lastName: '',
      emailAddress: '',
      addressLine1: c?.address ?? '',
      city: c?.town ?? '',
      region: c?.county ?? '',
      postalCode: c?.postcode ?? '',
      country: c?.country ?? ''
    };
    this.isCreatingFinanceContact = true;
  }

  cancelCreateFinanceContact() {
    this.isCreatingFinanceContact = false;
  }

  saveNewFinanceContact() {
    if (!this.financeClientLookupClient) return;
    if (!this.newFinanceContact.name?.trim()) {
      alert('Contact Name is required.');
      return;
    }
    this.isCreatingFinanceContactSaving = true;

    this.http.post<{ contactId: string }>(
      `/charitycentral/CreateFinanceContact?UserId=${encodeURIComponent(this.loggedInUserTag)}`,
      this.newFinanceContact
    ).subscribe({
      next: (result) => {
        this.isCreatingFinanceContactSaving = false;
        if (this.financeClientLookupClient) {
          this.financeClientLookupClient.financeClientId = result.contactId;
          this.financeClientLookupClient.financeClientName = this.newFinanceContact.name;
        }
        this.closeFinanceClientLookup();
      },
      error: (err) => {
        this.isCreatingFinanceContactSaving = false;
        if (err.status === 401) {
          alert('Xero is not authorised. Please reauthorise Xero in the Finance settings before creating contacts.');
        } else {
          alert(`Failed to create Xero contact: ${err.error?.message || err.message}`);
        }
      }
    });
  }

  formatFinanceContactAddresses(contact: any): string {
    const addresses = contact?.Addresses as any[] | undefined;
    if (!addresses || addresses.length === 0) return '';
    return addresses
      .map(a => [a.AddressLine1, a.AddressLine2, a.City, a.Region, a.PostalCode, a.Country]
        .filter(p => p && String(p).trim().length > 0)
        .join(', '))
      .filter(s => s.length > 0)
      .join(' | ');
  }

  // Text typed in the "Associated Beneficiary" input before opening the lookup popup
  beneficiaryNameInput: string = '';

  // Inline beneficiary lookup UI (replaces external dialog usage for SearchURN)
  isBeneficiaryPopupOpen = false;
  beneficiarySearchQuery = {
    beneficiaryName: '',
    beneficiaryURN: '',
    address: '',
    postcode: ''
  };
  beneficiarySearchResults: any[] = [];
  selectedBeneficiaryId: number | null = null; // single select (use numeric id)
  showOnlySelectedBeneficiaries = false;
  tooltipVisible: Record<number, boolean> = {};
  tooltipContent: Record<number, { aliases: string[]; previousNames: string[] }> = {};
  tooltipPosition: Record<number, { top: number; left: number }> = {};
  private tooltipHideTimeouts: { [key: number]: any } = {};
  private isMouseOverTooltip: { [key: number]: boolean } = {};

  // Track client for whom the lookup is open
  private beneficiaryLookupClient: Client | null = null;

  // New properties/methods used by template for adding beneficiaries
  showAddBeneficiaryForm = false;
  newBeneficiary: any = {};
  alert: { text: string; type: string } = { text: '', type: '' };

  toggleAddBeneficiaryForm() { this.showAddBeneficiaryForm = !this.showAddBeneficiaryForm; }

  saveNewBeneficiary() {
    // Create a temporary beneficiary entry and select it
    const generatedId = Math.floor(Math.random() * 1000000);
    const b = {
      id: generatedId,
      name: this.newBeneficiary.name || 'New Beneficiary',
      beneficiarySourceURN: this.newBeneficiary.beneficiarySourceURN || String(generatedId),
      addressLine1: this.newBeneficiary.addressLine1 || '',
      addressLine2: this.newBeneficiary.addressLine2 || '',
      postcode: this.newBeneficiary.postcode || '',
      email: this.newBeneficiary.email || ''
    };

    // Add to results and select single
    this.beneficiarySearchResults = [b, ...(this.beneficiarySearchResults || [])];
    this.selectedBeneficiaryId = b.id;
    // update local map for immediate UI display
    this.associatedBeneficiaryMap[b.id] = `${b.name} (${b.beneficiarySourceURN})`;
    this.alert = { text: 'Beneficiary added successfully', type: 'success' };
    this.showAddBeneficiaryForm = false;
    this.newBeneficiary = {};
  }

  trackById = (_: number, item: { id: number }) => item.id;

  openBeneficiaryLookup(client: Client) {
    // Prepare state for inline overlay
    this.beneficiaryLookupClient = client;
    this.selectedBeneficiaryId = client.associatedBeneficiaryId == null || client.associatedBeneficiaryId === 0 ? null : client.associatedBeneficiaryId as number;
    this.beneficiarySearchQuery = { beneficiaryName: this.beneficiaryNameInput || '', beneficiaryURN: '', address: '', postcode: '' };
    this.beneficiarySearchResults = [];
    this.showOnlySelectedBeneficiaries = false;
    this.tooltipVisible = {};
    this.tooltipContent = {};
    this.tooltipPosition = {};
    this.isBeneficiaryPopupOpen = true;

    // Auto-search if user typed a name before opening
    if (this.beneficiaryNameInput.trim()) {
      this.onBeneficiarySearch();
    }
  }

  closeBeneficiaryLookup() {
    this.isBeneficiaryPopupOpen = false;
    this.beneficiaryLookupClient = null;
    this.selectedBeneficiaryId = null;
    this.beneficiarySearchResults = [];
  }

  openAddBeneficiaryDialog() {
    const ref = this.dialog.open(AdminBeneficiaryDialogComponent, {
      width: '900px',
      maxHeight: '90vh',
      data: {
        beneficiary: {
          id: 0,
          name: this.beneficiaryNameInput || '',
          beneficiarySourceURN: '',
          active: true,
          isClient: true,
          source: { id: 0, name: '', code: '' }
        }
      }
    });

    ref.afterClosed().subscribe(result => {
      if (!result) return;

      const normalizeArray = (val: any): string[] => {
        if (!val) return [];
        if (Array.isArray(val)) return val.map(v => String(v));
        if (typeof val === 'string') return val.split(',').map((s: string) => s.trim()).filter(Boolean);
        return [];
      };

      const payload: any = {
        ...result,
        active: result.active ?? true,
        isClient: result.isClient ?? true,
        aliases: normalizeArray(result.aliases),
        source: result.source ?? { id: 1, name: 'Manual Entry', code: 'MAN' },
        previousNames: normalizeArray(result.previousNames)
      };

      const params = new HttpParams().set('user', this.loggedInUserTag);
      this.http.post<number>('/charitycentral/addbeneficiary', payload, { params })
        .subscribe(newId => {
          const newBeneficiary = { ...payload, id: newId };
          this.beneficiarySearchResults = [newBeneficiary, ...this.beneficiarySearchResults];
          this.ensureAliasesAndPreviousNames();
          this.selectedBeneficiaryId = newId;

          // Immediately update the client so saving works regardless of how the popup is closed
          if (this.beneficiaryLookupClient) {
            this.beneficiaryLookupClient.associatedBeneficiaryId = newId;
            const displayName = `${payload.name} (${payload.beneficiarySourceURN || newId})`;
            this.beneficiaryLookupClient.associatedBeneficiaryName = displayName;
            this.associatedBeneficiaryMap[newId] = displayName;
          }
        });
    });
  }

  onBeneficiarySearchKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') { event.preventDefault(); this.onBeneficiarySearch(); }
  }

  onBeneficiarySearch() {
    if (this.showOnlySelectedBeneficiaries) {
      // If show only selected, and client has an associated beneficiary, fetch that single beneficiary
      const client = this.beneficiaryLookupClient;
      if (!client || !client.associatedBeneficiaryId) {
        this.beneficiarySearchResults = [];
        return;
      }
      // fetch by numeric id
      const params = new HttpParams().set('id', String(client.associatedBeneficiaryId)).set('user', this.loggedInUserTag);;
      this.http.get<any[]>('/charitycentral/getbeneficiaryById', { params })
        .subscribe(results => {
          this.beneficiarySearchResults = results || [];
          this.ensureAliasesAndPreviousNames();
        });
      return;
    }

    const params = new HttpParams()
      .set('beneficiaryname', this.beneficiarySearchQuery.beneficiaryName || '')
      .set('beneficiaryaliasname', this.beneficiarySearchQuery.beneficiaryName || '')
      .set('beneficiaryurn', this.beneficiarySearchQuery.beneficiaryURN || '')
      .set('address', this.beneficiarySearchQuery.address || '')
      .set('postcode', this.beneficiarySearchQuery.postcode || '');

    this.http.get<any[]>('/charitycentral/getbeneficiaries', { params })
      .subscribe(results => {
        this.beneficiarySearchResults = results || [];
        this.ensureAliasesAndPreviousNames();
      });
  }

  private ensureAliasesAndPreviousNames() {
    this.beneficiarySearchResults.forEach(r => {
      if (!Array.isArray(r.aliases)) {
        if (typeof r.aliases === 'string' && r.aliases.trim().length >0) r.aliases = r.aliases.split(',').map((s: string) => s.trim()).filter((s: string) => s.length >0);
        else r.aliases = [];
      }
      if (!Array.isArray(r.previousNames)) {
        if (typeof r.previousNames === 'string' && r.previousNames.trim().length > 0) r.previousNames = r.previousNames.split(',').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
        else r.previousNames = [];
      }

      // map by numeric id for UI display
      const id = Number(r.id || 0);
      const display = `${r.name} (${r.beneficiarySourceURN})`;
      if (id) this.associatedBeneficiaryMap[id] = display;
    });
    this.tooltipVisible = {};
    this.tooltipContent = {};
    this.tooltipPosition = {};
    this.tooltipHideTimeouts = {};
    this.isMouseOverTooltip = {};
  }

  toggleShowOnlySelectedBeneficiaries() {
    this.showOnlySelectedBeneficiaries = !this.showOnlySelectedBeneficiaries;
    if (this.showOnlySelectedBeneficiaries) {
      // Trigger a search that returns only the currently associated beneficiary for this client
      this.onBeneficiarySearch();
      // pre-select if available
      if (this.beneficiaryLookupClient && this.beneficiaryLookupClient.associatedBeneficiaryId) this.selectedBeneficiaryId = this.beneficiaryLookupClient.associatedBeneficiaryId as number;
    } else {
      this.beneficiarySearchResults = [];
      this.selectedBeneficiaryId = this.beneficiaryLookupClient && this.beneficiaryLookupClient.associatedBeneficiaryId ? this.beneficiaryLookupClient.associatedBeneficiaryId as number : null;
    }
  }

  // Single-select behavior: clicking a row selects that beneficiary (radio-like)
  selectBeneficiary(result: any) {
    const id = result.id;
    if (id === undefined || id === null) return;
    if (this.selectedBeneficiaryId === id) this.selectedBeneficiaryId = null; else this.selectedBeneficiaryId = id;
  }

  saveBeneficiarySelection() {
    if (!this.beneficiaryLookupClient) { this.closeBeneficiaryLookup(); return; }
    const id = this.selectedBeneficiaryId;
    if (id === null || id === undefined) this.beneficiaryLookupClient.associatedBeneficiaryId = undefined as any;
    else this.beneficiaryLookupClient.associatedBeneficiaryId = id as number;

    // Fetch details for display and cache by numeric id
    if (this.selectedBeneficiaryId !== null && this.selectedBeneficiaryId !== undefined) {
      const idParam = new HttpParams().set('id', String(this.selectedBeneficiaryId)).set('user', this.loggedInUserTag);
      this.http.get<any[]>('/charitycentral/GetBeneficiaryById', { params: idParam }).subscribe(res => {
        const found = (res || [])[0];
        if (found) {
          const idNum = Number(found.id || 0);
          const display = `${found.name} (${found.beneficiarySourceURN})`;
          if (idNum) {
            this.associatedBeneficiaryMap[idNum] = display;
            if (this.beneficiaryLookupClient) this.beneficiaryLookupClient.associatedBeneficiaryName = display;
          }
        }
      }, () => { /* ignore */ });
    } else {
      if (this.beneficiaryLookupClient) this.beneficiaryLookupClient.associatedBeneficiaryName = undefined as any;
    }

    this.closeBeneficiaryLookup();
  }

  // Overlay handler to avoid template EventTarget typing issues
  onBeneficiaryOverlayMouseDown(event: MouseEvent) {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    if (target.classList && target.classList.contains('charity-lookup-overlay')) this.closeBeneficiaryLookup();
  }

  // Tooltip helpers (kept similar to existing subscription dialog implementation)
  showTooltip(index: number, aliases: string[], previousNames: string[], cell: HTMLElement) {
    if (this.tooltipHideTimeouts[index]) { clearTimeout(this.tooltipHideTimeouts[index]); this.tooltipHideTimeouts[index] = null; }
    this.tooltipVisible[index] = true;
    this.tooltipContent[index] = { aliases: aliases || [], previousNames: previousNames || [] };
    const rect = cell.getBoundingClientRect();
    const offset = 8;
    const tooltipWidth = 420;
    const maxItems = Math.max(aliases?.length || 0, previousNames?.length || 0);
    const tooltipHeight = Math.min(maxItems * 28 + 80, 400);
    let top = rect.bottom + offset;
    if (top + tooltipHeight > window.innerHeight - 20) top = rect.top - tooltipHeight - offset;
    let left = rect.left;
    const maxLeft = window.innerWidth - tooltipWidth - 20;
    if (left > maxLeft) left = maxLeft;
    if (left < 8) left = 8;
    this.tooltipPosition[index] = { top, left };
  }

  hideTooltip(index: number) {
    this.tooltipHideTimeouts[index] = setTimeout(() => {
      if (!this.isMouseOverTooltip[index]) this.tooltipVisible[index] = false;
    }, 150);
  }
  onTooltipMouseEnter(index: number) { this.isMouseOverTooltip[index] = true; if (this.tooltipHideTimeouts[index]) { clearTimeout(this.tooltipHideTimeouts[index]); this.tooltipHideTimeouts[index] = null; } }
  onTooltipMouseLeave(index: number) { this.isMouseOverTooltip[index] = false; this.tooltipVisible[index] = false; }

  getAssociatedBeneficiaryDisplay(client: Client) {
    if (!client.associatedBeneficiaryId) return 'No Associated Beneficiary';
    // Prefer explicit name returned from backend or set locally
    if (client.associatedBeneficiaryName) return `${client.associatedBeneficiaryName} (${client.associatedBeneficiaryId})`.replace(/\s+\(/, ' (')
    const id = Number(client.associatedBeneficiaryId);
    if (this.associatedBeneficiaryMap[id]) return this.associatedBeneficiaryMap[id];
    return `${client.associatedBeneficiaryId}`;
  }
}
